import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import type { PricingQuote } from '../lib/pricingDisplay';

export function usePricingQuote(currency: 'jpy' | 'usd', enabled = true) {
  const userId = useAuthStore((state) => state.user?.id);
  const [result, setResult] = useState<{ key: string; quote: PricingQuote } | null>(null);
  const key = `${currency}:${userId ?? 'anonymous'}`;
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    void (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {};
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      const response = await fetch(`/api/pricing-region?currency=${currency}`, {
        headers, signal: controller.signal, cache: 'no-store',
      });
      if (!response.ok) return;
      const quote = await response.json() as PricingQuote;
      if (!controller.signal.aborted) setResult({ key, quote });
    })().catch(() => { /* Checkout remains available if the quote cannot load. */ });
    return () => controller.abort();
  }, [currency, enabled, key]);
  // Never show a previous account's eligibility or the previous currency's
  // quote while a new request is in flight.
  return result?.key === key ? result.quote : null;
}
